<script lang="ts">
import { defineComponent, h, unref, watch } from 'vue';

import { useInjectLayout } from '@components/components/CdsLayout/context';
import { classNames, hUniqueSlot, warn } from '@components/utils';

import {
  makeInvertColor,
  makeLayoutProps,
  makeThemeProps,
  makeTransparentProps,
  provideTheme,
  useInvertColor,
  useLayout,
  useRender,
  useTransparent
} from '../../composable';
import { LayoutResizeObserver } from '../CdsLayout/components';

export const componentName = 'CdsHeader';
export default defineComponent({
  name: componentName,
  props: {
    ...makeLayoutProps(),
    ...makeInvertColor(),
    ...makeTransparentProps(),
    ...makeThemeProps()
  },
  emits: {
    reveal: (value: boolean) => true,
    focusin: (event: FocusEvent) => true
  },
  setup(props, { slots, emit, expose }) {
    const layout = useInjectLayout();
    const { themeClasses } = provideTheme(props);
    const { invertColorClasses } = useInvertColor(props, componentName);
    const { transparentClasses } = useTransparent(props, componentName);

    if (!Object.keys(layout).length) {
      warn('Header', `CdsHeader component must be used inside of a CdsLayout component.`);
      return () => {};
    }

    const { revealed, revealOnFocus, classes, style, updateLocal, onResize } = useLayout(props, componentName, 'H');

    function onFocusin(event: FocusEvent) {
      if (unref(revealOnFocus)) {
        updateLocal(revealed, true);
      }

      emit('focusin', event);
    }

    watch(revealed, (value) => {
      layout.animate();
      emit('reveal', value);
    });

    watch(layout.scroll, (scroll) => {
      props.reveal &&
        updateLocal(
          revealed,
          scroll.direction === 'up' ||
            scroll.position.top <= props.revealOffset ||
            scroll.position.top - scroll.inflectionPoint.top < 100
        );
    });

    useRender(() => {
      const child = hUniqueSlot(slots.default, []);

      child.push(
        h(LayoutResizeObserver, {
          debounce: 0,
          onResize
        })
      );

      return h(
        'header',
        {
          class: classNames(unref(classes), unref(invertColorClasses), unref(transparentClasses), unref(themeClasses)),
          style: unref(style),
          onFocusin
        },
        child
      );
    });

    expose({
      ...layout
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsHeader';
</style>
